import { useCallback, useEffect, useState } from 'react'
import { useLocation } from './route'

/**
 * Drop-in replacement for `@mantine/hooks` `useHash`, backed by React Router's
 * `useLocation().hash` instead of raw `window.location.hash` + `hashchange`.
 *
 * Why: mantine's implementation only learns about hash changes through the
 * `hashchange` DOM event, which `history.replaceState` / `history.pushState`
 * never fire. React Router's `navigate()` uses those History APIs internally,
 * so mantine's hook stays stale after every React Router navigation that
 * includes a hash — which in SSR Framework Mode happens asynchronously after
 * server loader data loads, causing URL oscillation.
 *
 * This hook merges two sources of truth:
 *  - `useLocation().hash` — updates when React Router commits a navigation
 *  - `localHash` state  — updates immediately on `setHash()` calls, so the
 *    UI reacts without waiting for a round-trip
 *
 * `setHash()` writes via `window.location.hash` (fires `hashchange`, avoids
 * React Router data revalidation) and is referentially stable (`useCallback`).
 */
export function useHash(): [hash: string, setHash: (value: string) => void] {
  const { hash: locationHash } = useLocation()

  // Immediate hash set via setHash(), cleared when React Router catches up.
  const [localHash, setLocalHash] = useState<string | null>(null)

  // When React Router commits a navigation whose hash matches or supersedes
  // the local value, drop the override so we read from the router again.
  useEffect(() => {
    setLocalHash(null)
  }, [locationHash])

  // Pick up hash changes that bypass both React Router and our setHash()
  // (e.g. user clicking an in-page anchor link).
  useEffect(() => {
    const onHashChange = () => setLocalHash(window.location.hash)
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const hash = localHash ?? locationHash

  const setHash = useCallback((value: string) => {
    const normalized = value.startsWith('#') ? value : `#${value}`
    if (normalized === window.location.hash) {
      // Already on this hash — nothing to write, and skipping the
      // pushState below also avoids a redundant `hashchange` round-trip.
      setLocalHash(normalized)
      return
    }
    const oldURL = window.location.href
    const newURL = `${window.location.pathname}${window.location.search}${normalized}`
    // Why pushState instead of `window.location.hash = normalized`:
    // assigning to `window.location.hash` runs the browser's
    // "navigate to fragment" algorithm. When the new hash doesn't
    // match an element `id` (which is true for our cursor-paging
    // hashes like `#after=hKJpZNcAG…&type=artwork&sort=latest`),
    // step "indicated part is no part" in the HTML spec tells the
    // user agent to "scroll the document to the start of the
    // document" — i.e. snap to (0,0). The visible symptom: on
    // `/zh/@<user>/artworks`, scrolling down to the bottom and
    // clicking "Next" yanks the viewport back to the top of the
    // profile page, hiding the very pagination button the user just
    // clicked. `history.pushState` updates the URL bar + adds a
    // history entry without invoking the fragment-navigation
    // algorithm, so the scrollTop stays put. We then dispatch a
    // synthetic `hashchange` so downstream listeners (the effect a
    // few lines below, mantine `useHash`, GTM, etc.) still see the
    // transition — `pushState` doesn't emit `hashchange` on its own.
    // React Router's `<ScrollRestoration />` is keyed off
    // `useLocation()` updates driven by `popstate`, which neither
    // `pushState` nor `hashchange` fires, so it doesn't interfere
    // here either. Back/forward (which do fire `popstate`) still
    // route through React Router and restore scroll as usual.
    window.history.pushState(null, '', newURL)
    setLocalHash(normalized)
    window.dispatchEvent(
      new HashChangeEvent('hashchange', {
        oldURL,
        newURL: window.location.href,
      }),
    )
  }, [])

  return [hash, setHash]
}
